/**
 * Les demandes de validation : ce qu'on m'a demandé, et ce que j'ai demandé.
 *
 * *Demande du métier, 15/08/2026 : « envoi du mail du conducteur pour avertir responsable
 * transport, puis validation des transports par le responsable dans le planning, puis soumission
 * de la contre-étude et planning par le conducteur au responsable exploitation ».*
 *
 * ## Ce qui m'attend passe en tête
 *
 * Un responsable ouvre cet écran pour répondre, pas pour consulter. Ses demandes en attente sont
 * donc en premier ; l'historique — le sien et, pour l'administrateur, celui de tout le monde —
 * vient après. C'est le même parti pris que l'écran des membres, où les approbations à faire ne
 * sont jamais noyées dans la liste complète.
 *
 * ## Les montants affichés sont ceux de la DEMANDE
 *
 * Ils viennent de la ligne, pas d'un recalcul : le responsable doit lire à l'écran les chiffres
 * exacts de son courriel. Un total recalculé à l'affichage raconterait autre chose que ce qu'on
 * lui a demandé de valider, et rien ne le lui dirait.
 *
 * ## Ce que l'écran ne décide pas
 *
 * Qui peut répondre est décidé par le serveur (`moi.estAdmin`, `destinataire.id`), qui refuse de
 * toute façon. L'écran s'y conforme pour ne pas proposer un bouton qui échouera — il ne s'y
 * substitue pas.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { CreationsArticles } from "./CreationsArticles";
import { formaterMontant } from "@planif/moteur";
import {
  annulerSoumission,
  chargerSoumissions,
  relancerSoumission,
  repondreSoumission,
} from "./api";
import type { Soumission, TableauSoumissions } from "./api";
import { ecartAffichable } from "./ecart";

const LIBELLE_STATUT: Record<Soumission["statut"], string> = {
  en_attente: "En attente",
  accepte: "Accepté",
  refuse: "Refusé",
  annule: "Annulé",
};

function formaterDate(iso: string | null): string {
  if (iso === null || iso === "") return "—";
  const d = new Date(iso);
  return (
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/` +
    `${String(d.getFullYear())} à ${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`
  );
}

/** Depuis combien de jours cette demande attend. C'est l'information qu'un rappel rend visible. */
function anciennete(iso: string): string {
  const jours = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return "depuis hier";
  return `depuis ${String(jours)} jours`;
}

function euros(valeur: string | null): string {
  return valeur === null ? "—" : `${formaterMontant(valeur)} €`;
}

/**
 * L'écart au devis figé, en une phrase.
 *
 * Toujours contre le devis figé, jamais contre la version précédente : c'est la règle métier du
 * 10/08 — ce qu'on veut savoir est le gain contre *ce qui a été vendu*, une grandeur unique.
 */
function ecart(valeur: string | null, reference: string | null): JSX.Element | null {
  if (valeur === null || reference === null) return null;
  const v = Number(valeur);
  const r = Number(reference);
  // Sans référence non nulle, il n'y a pas de pourcentage à écrire — et un écart sans son
  // pourcentage se lirait comme un montant de plus sur la ligne.
  if (!Number.isFinite(v) || !Number.isFinite(r) || r === 0) return null;
  const delta = ecartAffichable(v - r);
  if (delta === null) return <span className="vide"> (identique au devis)</span>;
  const pct = (((v - r) / r) * 100).toFixed(2);
  return (
    <span className={delta.classe}>
      {` ${delta.signe}${delta.montant} € (${delta.signe === "−" ? "" : "+"}${pct} %)`}
    </span>
  );
}

function Chiffres({ s }: { s: Soumission }): JSX.Element {
  return (
    <div className="soumission__chiffres">
      <span>
        Déboursé <strong>{euros(s.debourse)}</strong>
        {ecart(s.debourse, s.debourseDevis)}
      </span>
      <span>
        Vente <strong>{euros(s.vente)}</strong>
        {ecart(s.vente, s.venteDevis)}
      </span>
    </div>
  );
}

function Entete({ s, circuits }: { s: Soumission; circuits: Record<string, string> }): JSX.Element {
  return (
    <div className="soumission__entete">
      <span className="soumission__etape">{s.etape}</span>
      <strong>{circuits[s.circuit] ?? s.circuit}</strong>
      <span>
        {s.chantierApo} — {s.chantierNom}
      </span>
      <span className="vide">
        {s.versionRang === null ? "" : `V${String(s.versionRang)} `}
        {s.versionLibelle ?? "(sans nom)"}
        {s.versionFigee ? " · figée" : ""}
      </span>
    </div>
  );
}

/**
 * Accepter ou refuser — les deux seuls boutons qui ferment une demande.
 *
 * Extrait pour être posé à DEUX endroits : sur ce qui m'est adressé, et sur ce qu'un
 * administrateur traite à la place d'un absent. Le serveur autorise les deux depuis le premier
 * jour ; ne les proposer qu'au destinataire laissait l'administrateur devant une demande qu'il
 * avait le droit de trancher, sans aucun bouton pour le faire.
 *
 * `pourAutrui` ne change pas le geste, il change ce qu'on en dit : répondre à la place de
 * quelqu'un doit se lire comme tel, sur le bouton et non dans une note à côté.
 */
function GestesDeReponse({
  s,
  occupe,
  agir,
  pourAutrui,
}: {
  s: Soumission;
  occupe: string | null;
  agir: (promesse: Promise<unknown>, id: string) => void;
  pourAutrui: boolean;
}): JSX.Element {
  const [motif, setMotif] = useState("");
  const [refusEnCours, setRefusEnCours] = useState(false);
  const aLaPlaceDe = pourAutrui ? ` à la place de ${s.destinataire.prenom} ${s.destinataire.nom}` : "";

  return (
    <>
      {!refusEnCours ? (
        <div className="soumission__gestes">
          <button
            type="button"
            disabled={occupe !== null}
            onClick={() => {
              agir(repondreSoumission(s.id, true, null), s.id);
            }}
          >
            Accepter{aLaPlaceDe}
          </button>
          <button
            type="button"
            className="bouton-danger"
            disabled={occupe !== null}
            onClick={() => {
              setRefusEnCours(true);
            }}
          >
            Refuser…
          </button>
        </div>
      ) : (
        <div className="soumission__refus">
          <label>
            Pourquoi ? Sans motif, il n'y a rien à corriger avant de resoumettre.
            <textarea
              rows={2}
              value={motif}
              onChange={(e) => {
                setMotif(e.target.value);
              }}
            />
          </label>
          <div className="soumission__gestes">
            <button
              type="button"
              className="bouton-danger"
              disabled={occupe !== null || motif.trim() === ""}
              onClick={() => {
                agir(repondreSoumission(s.id, false, motif.trim()), s.id);
              }}
            >
              Confirmer le refus
            </button>
            <button
              type="button"
              onClick={() => {
                setRefusEnCours(false);
                setMotif("");
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Une demande à laquelle JE dois répondre. */
function ARepondre({
  s,
  circuits,
  occupe,
  agir,
}: {
  s: Soumission;
  circuits: Record<string, string>;
  occupe: string | null;
  agir: (promesse: Promise<unknown>, id: string) => void;
}): JSX.Element {
  return (
    <li className="soumission soumission--a-repondre">
      <Entete s={s} circuits={circuits} />
      <p className="soumission__qui">
        {s.demandeur.prenom} {s.demandeur.nom} vous demande de valider — {formaterDate(s.creeLe)},{" "}
        {anciennete(s.creeLe)}
        {s.relanceLe === null ? "" : ` · rappel envoyé le ${formaterDate(s.relanceLe)}`}
      </p>
      <Chiffres s={s} />
      {s.message !== null && s.message !== "" && <p className="soumission__mot">« {s.message} »</p>}
      {/*
        **Ce que le conducteur a prévu POUR VOUS** (051, demande du 27/08/2026).

        Distinct du mot ci-dessus, et étiqueté comme tel : celui-là accompagne cet envoi — « ce
        qui a changé » —, celui-ci vit sur la version et dit ce qui est prévu. Il est ici parce
        que c'est ici qu'on décide : une consigne qu'il faudrait aller chercher sur la
        contre-étude ne serait pas lue avant de répondre.
      */}
      {s.commentaire !== null && s.commentaire !== "" && (
        <p className="soumission__prevu">
          <strong>Ce qui est prévu pour vous :</strong> {s.commentaire}
        </p>
      )}
      <GestesDeReponse s={s} occupe={occupe} agir={agir} pourAutrui={false} />
    </li>
  );
}

/** Une demande que J'AI envoyée, ou que je consulte en tant qu'administrateur. */
function Envoyee({
  s,
  circuits,
  occupe,
  agir,
  monId,
  estAdmin,
}: {
  s: Soumission;
  circuits: Record<string, string>;
  occupe: string | null;
  agir: (promesse: Promise<unknown>, id: string) => void;
  monId: string;
  estAdmin: boolean;
}): JSX.Element {
  const mienne = s.demandeur.id === monId || estAdmin;
  const attend = s.statut === "en_attente";

  return (
    <li className={`soumission soumission--${s.statut}`}>
      <Entete s={s} circuits={circuits} />
      <p className="soumission__qui">
        <span className={`soumission__statut soumission__statut--${s.statut}`}>
          {LIBELLE_STATUT[s.statut]}
        </span>
        {" · "}
        {s.demandeur.prenom} {s.demandeur.nom} → {s.destinataire.prenom} {s.destinataire.nom}
        {" · "}
        {formaterDate(s.creeLe)}
        {attend ? ` · ${anciennete(s.creeLe)}` : ` · répondu le ${formaterDate(s.reponduLe)}`}
      </p>
      <Chiffres s={s} />
      {s.motif !== null && s.motif !== "" && (
        <p className="soumission__mot">Motif : « {s.motif} »</p>
      )}

      {attend && mienne && (
        <>
          <p className="note">
            En attente de {s.destinataire.prenom} {s.destinataire.nom}. Le rappel lui renvoie le
            même courriel sans effacer l'ancienneté de la demande ; l'annulation la retire.
          </p>
          <div className="soumission__gestes">
            <button
              type="button"
              disabled={occupe !== null}
              onClick={() => {
                agir(relancerSoumission(s.id), s.id);
              }}
            >
              Renvoyer le courriel à {s.destinataire.prenom} {s.destinataire.nom}
            </button>
            <button
              type="button"
              className="bouton-danger"
              disabled={occupe !== null}
              onClick={() => {
                agir(annulerSoumission(s.id), s.id);
              }}
            >
              Annuler la demande
            </button>
          </div>
        </>
      )}

      {/* Répondre à la place d'un absent : le serveur ne l'accorde qu'à l'administrateur, et il
          le vérifie lui-même. Sans ces boutons ici, il voyait la demande sans pouvoir la
          trancher — alors que débloquer une absence est précisément son rôle. */}
      {attend && estAdmin && s.destinataire.id !== monId && (
        <GestesDeReponse s={s} occupe={occupe} agir={agir} pourAutrui />
      )}
    </li>
  );
}

export function Soumissions({
  monId,
  onRepondu,
}: {
  monId: string;
  /** Prévient l'application qu'une réponse a pu changer l'arbre des versions d'un chantier. */
  onRepondu: () => void;
}): JSX.Element {
  const [tableau, setTableau] = useState<TableauSoumissions | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  const recharger = (): void => {
    chargerSoumissions()
      .then((r) => {
        setTableau(r);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  };

  useEffect(recharger, []);

  const agir = (promesse: Promise<unknown>, id: string): void => {
    setOccupe(id);
    setErreur(null);
    promesse
      .then(() => {
        recharger();
        // Accepter côté exploitation fige la version et crée la CE de suivi : la liste des
        // chantiers, qui alimente le sélecteur de versions, ne le sait pas sans qu'on le lui dise.
        onRepondu();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  if (erreur !== null && tableau === null) {
    return (
      <section className="referentiel">
        <h2>Soumissions</h2>
        <p className="alerte" role="alert">
          {erreur}
        </p>
      </section>
    );
  }
  if (tableau === null) {
    return (
      <section className="referentiel">
        <h2>Soumissions</h2>
        <p className="etat">Chargement…</p>
      </section>
    );
  }

  const circuits = tableau.circuits as unknown as Record<string, string>;
  const aRepondre = tableau.soumissions.filter(
    (s) => s.statut === "en_attente" && s.destinataire.id === monId,
  );
  const reste = tableau.soumissions.filter((s) => !aRepondre.includes(s));

  return (
    <section className="referentiel">
      <h2>Soumissions — {String(tableau.soumissions.length)}</h2>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {/* LES ARTICLES À CRÉER, EN TÊTE (071).

          C'est ici que l'assistante référente apprend qu'une création l'attend : elle vient déjà
          dans cet écran pour répondre aux contre-études, et une demande d'article est une demande
          de plus qu'on lui adresse. Elle vivait dans l'écran Stocks jusqu'au 08/09/2026, où elle
          n'avait aucun moyen de la découvrir sans y passer d'elle-même. */}
      <CreationsArticles />

      <h3>
        À valider par vous
        {aRepondre.length === 0 ? "" : ` — ${String(aRepondre.length)}`}
      </h3>
      {aRepondre.length === 0 ? (
        <p className="etat">Rien ne vous attend.</p>
      ) : (
        <ul className="soumissions">
          {aRepondre.map((s) => (
            <ARepondre key={s.id} s={s} circuits={circuits} occupe={occupe} agir={agir} />
          ))}
        </ul>
      )}

      <h3>{tableau.moi.estAdmin ? "Toutes les demandes" : "Vos demandes"}</h3>
      {reste.length === 0 ? (
        <p className="etat">
          Aucune demande. Une contre-étude se soumet depuis son écran, bouton « Soumettre ».
        </p>
      ) : (
        <ul className="soumissions">
          {reste.map((s) => (
            <Envoyee
              key={s.id}
              s={s}
              circuits={circuits}
              occupe={occupe}
              agir={agir}
              monId={monId}
              estAdmin={tableau.moi.estAdmin}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
